import { FormStore, GlobalStore, GridStore } from '@roc/feature-app-core';
import { action, computed, flow, makeObservable, observable } from 'mobx';
import {
  DateFormat,
  formatDate,
  GENERIC_ERROR_MESSAGE,
  parseAddress,
} from '@roc/feature-utils';
import { SearchEntityGridStore } from './searchEntityGridStore';
import { LoanSubType, SelectEntityOption } from '../../utils/constants';
import { BorrowersStore } from '../common/borrowersStore';
import { mapFromAddressField, mapToAddressField } from '../../utils/quoteUtils';
import { QuoteService } from '../../services/quoteService';

const entityForm = {
  fields: {
    entityId: {
      value: '',
      error: null,
      rule: '',
    },
    entityName: {
      value: '',
      error: null,
      rule: 'required',
      message: 'Required',
    },
    entityEin: {
      value: '',
      error: null,
      rule: 'required|regex:/^\\d{2}-?\\d{7}$/|alpha_dash',
      message: 'EIN must be a number with the format xx-xxxxxxx',
    },
    entityType: {
      value: '',
      error: null,
      rule: 'required',
      message: 'Required',
    },
    dateOfIncorporation: {
      value: null,
      error: null,
      rule: 'required',
    },
    address: {
      value: null,
      error: null,
      rule: 'required',
    },
    hasEntity: {
      value: null,
      error: null,
      rule: 'required',
    },
  },
  meta: {
    key: null,
    editMode: false,
    isValid: false,
    error: null,
  },
};

export class EntityStore {
  private globalStore: GlobalStore;
  private quoteService: QuoteService;
  private borrowersStore: BorrowersStore;
  private loanSubtype: LoanSubType;
  public entityFormStore: FormStore;
  public entityDropdownData: any[];
  public borrowers: any[];
  public searchEntityGridStore: SearchEntityGridStore;
  public selectEntityOption: SelectEntityOption;
  public isBorrowerPercentageOwnershipEditable: boolean;

  constructor(
    globalStore,
    borrowersStore: BorrowersStore,
    loanSubtype: LoanSubType
  ) {
    this.globalStore = globalStore;
    this.borrowersStore = borrowersStore;
    this.loanSubtype = loanSubtype;
    this.quoteService = new QuoteService();
    this.entityFormStore = new FormStore(entityForm, globalStore);
    this.entityDropdownData = [];
    this.borrowers = [];
    this.isBorrowerPercentageOwnershipEditable = true;
    this.searchEntityGridStore = new SearchEntityGridStore(globalStore, this);

    makeObservable(this, {
      reset: action,
      entityDropdownData: observable,
      borrowers: observable,
      selectEntityOption: observable,
      getEntityDropdownData: flow,
      fetchEntityById: flow,
      setSelectEntityOption: action,
      loadEntityData: action,
      loadEntity: action,
      getEntity: action,
      resetSteps: action,
      clearEntityData: action,
      clearSelectedBorrowers: action,
      showOwnershipPercentage: computed,
      isBorrowerPercentageOwnershipEditable: observable,
    });
  }

  setSelectEntityOption(option: SelectEntityOption) {
    if (this.selectEntityOption != option) {
      this.selectEntityOption = option;
      this.clearEntityData();
    }
  }

  clearEntityData() {
    this.entityFormStore.reset();
    const hasEntity = this.selectEntityOption != SelectEntityOption.NO_ENTITY;
    this.entityFormStore.onFieldChange('hasEntity', hasEntity);
  }

  clearSelectedBorrowers() {
    this.borrowersStore.resetBorrowerFormStores();
    this.borrowersStore.addNewBorrower();
  }

  *getEntityDropdownData() {
    try {
      const response = yield this.quoteService.getEntityDropdownData();
      this.entityDropdownData = response.data.data;
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }


  *fetchEntityById(entityId: number) {
    try {
      const borrowersStore = this.borrowersStore;
      const response = yield this.quoteService.getEntityById(entityId);
      const entity = response?.data?.data;
      const parsedAddress = parseAddress(entity?.entityAddress, undefined);

      this.loadEntity({
        ...entity,
        address: {
          ...parsedAddress,
          address:
            (parsedAddress.street_number || '') +
            `${parsedAddress.street ? ' ' + parsedAddress.street : ''}`,
        },
      });

      this.borrowers = Array.from(new Set(response.data.data.borrowers));

      borrowersStore.resetBorrowerFormStores();
      this.borrowers.forEach(borrower => {
        const borrowerFormStore = borrowersStore.createBorrowerForm();
        borrowerFormStore.loadExistingBorrower(borrower)
        borrowerFormStore.setEditMode(false);
        borrowersStore.addBorrower(borrowerFormStore);
      });

      if (entityId) {
        this.isBorrowerPercentageOwnershipEditable = false;
      }
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  loadEntityData(quoteData) {
    if(quoteData.uiState.selectEntityOption){
      this.selectEntityOption = quoteData.uiState.selectEntityOption;
    } else {
      this.resetSteps(quoteData);
    }
    this.loadEntity(quoteData.entity);
  }

  loadEntity(entity) {
    this.entityFormStore.loadForm({
      entityId: entity?.entityId ?? '',
      entityName: entity?.entityName ?? '',
      entityEin: entity?.entityEin ?? '',
      entityType: entity?.entityType ?? '',
      dateOfIncorporation: entity?.dateOfIncorporation ?? null,
      address: mapToAddressField(entity?.address),
      hasEntity: entity?.hasEntity ?? true,
    });
  }

  resetSteps(quoteData) {
    if (quoteData.entity?.entityId) {
      this.selectEntityOption = SelectEntityOption.SEARCH_ENTITY;
    } else if (quoteData.entity?.entityName || quoteData.entity?.entityEin) {
      this.selectEntityOption = SelectEntityOption.ADD_ENTITY;
    } else if (quoteData.borrowers?.[0]?.firstName) {
      this.selectEntityOption = SelectEntityOption.NO_ENTITY;
    } else {
      this.selectEntityOption = null;
    }
  }

  reset() {
    this.entityFormStore.reset();
    this.selectEntityOption = null;
    this.isBorrowerPercentageOwnershipEditable = true;
  }

  setHasEntity(value) {
    this.entityFormStore.reset();
    this.entityFormStore.onFieldChange('hasEntity', value);
  }

  getEntity() {
    const values = this.entityFormStore.getFormValues();
    return values.entityName
      ? {
          ...values,
          dateOfIncorporation: formatDate(
            values.dateOfIncorporation,
            DateFormat.MMDDYYYY
          ),
          address: mapFromAddressField(values.address),
        }
      : null;
  }

  validateEntity() {
    switch (this.selectEntityOption) {
      case SelectEntityOption.ADD_ENTITY:
        this.entityFormStore.runFormValidationWithMessage();
        return this.entityFormStore.form.meta.isValid;
      case SelectEntityOption.SEARCH_ENTITY:
        if (!this.entityFormStore.form.fields.entityId.value) {
          this.globalStore.notificationStore.showErrorNotification({
            message: 'You need to select an entity to continue.',
          });
          return false;
        }
        return true;
      case SelectEntityOption.NO_ENTITY:
        return true;
    }
  }

  get showOwnershipPercentage() {
    const { entityFormStore } = this;
    const hasEntity = entityFormStore.form.fields.hasEntity.value;
    return hasEntity && this.borrowersStore.borrowerFormStores.length > 1;
  }
}
